import React, { useEffect, useMemo, useRef, useState } from 'react';

export function MenuButton({ label, items }) {
  const [open, setOpen] = useState(false);
  const [focusIndex, setFocusIndex] = useState(0);
  const buttonRef = useRef(null);
  const menuRef = useRef(null);
  const menuId = useMemo(() => `menu-${Math.random().toString(36).slice(2, 10)}`, []);

  useEffect(() => {
    if (!open) return undefined;
    const firstItem = menuRef.current?.querySelector('[role="menuitem"]');
    firstItem?.focus();

    function handleDocumentClick(event) {
      if (!menuRef.current?.contains(event.target) && !buttonRef.current?.contains(event.target)) {
        setOpen(false);
      }
    }

    document.addEventListener('mousedown', handleDocumentClick);
    return () => document.removeEventListener('mousedown', handleDocumentClick);
  }, [open]);

  const handleButtonKeyDown = (event) => {
    if (event.key === 'ArrowDown' || event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      setOpen(true);
      setFocusIndex(0);
    }
  };

  const handleMenuKeyDown = (event) => {
    if (event.key === 'Escape') {
      event.preventDefault();
      setOpen(false);
      buttonRef.current?.focus();
      return;
    }

    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      event.preventDefault();
      const next = event.key === 'ArrowDown' ? focusIndex + 1 : focusIndex - 1;
      const wrappedIndex = (next + items.length) % items.length;
      setFocusIndex(wrappedIndex);
      const nextEl = menuRef.current?.querySelectorAll('[role="menuitem"]')[wrappedIndex];
      nextEl?.focus();
    }
  };

  const handleSelect = (item) => {
    setOpen(false);
    item.onSelect();
    buttonRef.current?.focus();
  };

  return (
    <div className="menu-button-wrap">
      <button
        ref={buttonRef}
        type="button"
        className="ui-button ui-button--secondary touch-target"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-controls={menuId}
        onClick={() => setOpen((prev) => !prev)}
        onKeyDown={handleButtonKeyDown}
      >
        {label}
      </button>
      {open ? (
        <div id={menuId} ref={menuRef} role="menu" className="menu-dropdown" onKeyDown={handleMenuKeyDown}>
          {items.map((item) => (
            <button
              key={item.id}
              type="button"
              role="menuitem"
              className="menu-dropdown-item"
              onClick={() => handleSelect(item)}
            >
              {item.label}
            </button>
          ))}
        </div>
      ) : null}
    </div>
  );
}
